import React, { useEffect, useState } from "react";
import { Card, Button, Typography, Modal, Drawer, Pagination, Tooltip, message } from "antd";
import {
  FolderOpenOutlined,
  EyeOutlined,
  RightOutlined,
  CalendarOutlined,
  UserOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  ProjectOutlined,
  FileTextOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axios from "../services/axios";
import moment from "moment";
import Logout from "../components/auth/Logout";

const { Title, Text } = Typography;

const LandingPage = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [imageSrc, setImageSrc] = useState("");
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const pageSize = 6;
  const user = localStorage.getItem("user");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const res = await axios.post("get-user-projects", { user });
        console.log("🚀 ~ fetchProjects ~ res:", res)
        if (res.data && res.data.projects) {
          const formattedProjects = res.data.projects.map((project) => ({
            ...project,
            projectImage: project.projectImage,
          }));
          setProjects(formattedProjects);
        } else {
          message.warning("No projects found.");
        }
      } catch (error) {
        message.error("Error fetching projects");
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const handleSelectProject = (project) => {
    setSelectedProject(project);
    localStorage.setItem("Project", JSON.stringify(project.projectName));
    navigate("/home");
  };

  const handleImageClick = (imageSrc) => {
    if (imageSrc) {
      setImageSrc(imageSrc);
      setImageModalVisible(true);
    } else {
      message.error("No image available");
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const paginatedProjects = projects.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-blue-200 rounded-full animate-spin border-t-blue-500 mx-auto mb-4"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-purple-200 rounded-full animate-ping mx-auto mt-2"></div>
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-gray-800">Loading Your Projects</h3>
            <p className="text-gray-600">Please wait a moment...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-200 p-4 sm:p-6 md:p-8 lg:p-10">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-8">
          <Title level={2} className="!text-2xl sm:!text-3xl md:!text-4xl font-bold text-gray-900">
            Project Dashboard
          </Title>
          <div className="mt-4 sm:mt-0">
            <Logout />
          </div>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedProjects.map((project, index) => (
            <Card
              key={project.projectCode}
              className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
              style={{ animation: `fadeIn ${0.3 + index * 0.1}s ease-out` }}
            >
              {/* Project Image */}
              <div className="relative overflow-hidden">
                <div
                  className="w-full h-48 cursor-pointer" 
                  onClick={() => handleImageClick(project.projectImage)}
                >
                  <img
                    src={project.projectImage}
                    alt={project.projectName}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <Tooltip title="View Image">
                    <div className="absolute top-3 right-3 bg-white bg-opacity-90 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-300">
                      <EyeOutlined className="text-teal-600 text-base" />
                    </div>
                  </Tooltip>
                </div>
              </div>

              {/* Project Content */}
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1 line-clamp-2 group-hover:text-teal-600 transition-colors">
                      {project.projectName}
                    </h3>
                    <Text className="text-gray-500 text-sm font-medium">{project.projectCode}</Text>
                  </div>
                </div>

                {/* Project Info */}
                <div className="space-y-2 mb-5">
                  <div className="flex items-center text-sm text-gray-600 truncate">
                    <UserOutlined className="mr-2 text-teal-600" />
                    <span className="font-medium">Manager:</span>
                    <span className="ml-1">{project.projectManager}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600 truncate">
                    <EnvironmentOutlined className="mr-2 text-teal-600" />
                    <span className="font-medium">Location:</span>
                    <span className="ml-1">{project.location}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600 truncate">
                    <TeamOutlined className="mr-2 text-teal-600" />
                    <span className="font-medium">Client:</span>
                    <span className="ml-1">{project.client}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    type="primary"
                    disabled={!project.active}
                    className={`flex-1 rounded-lg h-10 text-sm md:text-base font-medium ${
                      project.active
                        ? "bg-teal-600 hover:bg-teal-700 border-none"
                        : "bg-gray-300 border-none cursor-not-allowed"
                    }`}
                    icon={<RightOutlined />}
                    onClick={() => project.active && handleSelectProject(project)}
                  >
                    {project.active ? "Open Project" : "Inactive"}
                  </Button>
                  <Tooltip title="View Details">
                    <Button
                      className="border-teal-300 text-teal-600 hover:bg-teal-50 hover:border-teal-400 rounded-lg h-10 px-3 text-sm md:text-base transition-all hover:scale-105"
                      icon={<EyeOutlined />}
                      onClick={() => {
                        setSelectedProject(project);
                        setIsDrawerVisible(true);
                      }}
                    />
                  </Tooltip>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Pagination */}
        {projects.length > pageSize && (
          <div className="flex justify-center mt-10">
            <Pagination
              current={currentPage}
              total={projects.length}
              pageSize={pageSize}
              onChange={handlePageChange}
              showSizeChanger={false}
              className="bg-white rounded-lg shadow-sm px-4 py-2"
            />
          </div>
        )}
      </div>

      {/* Image Modal */}
       <Modal
          title={null}
          open={imageModalVisible}
          onCancel={() => setImageModalVisible(false)}
          footer={null}
          centered
          width="min(90vw, 700px)"
          className="rounded-2xl overflow-hidden"
        >
          <img src={imageSrc} alt="Project" className="w-full rounded-lg" />
        </Modal>

      {/* Project Details Drawer */}
      <Drawer
        title={
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
              <ProjectOutlined className="text-white text-lg" />
            </div>
            <div>
              <div className="font-bold text-xl text-gray-900">Project Details</div>
              <div className="text-sm text-gray-500">Complete project information</div>
            </div>
          </div>
        }
        placement="right"
        onClose={() => setIsDrawerVisible(false)}
        open={isDrawerVisible}
        width="min(90vw, 600px)"
        className="bg-gray-50"
      >
        {selectedProject && (
          <div className="space-y-6">
            {/* Project Image */}
            <div className="relative rounded-xl overflow-hidden shadow-md">
              <div
                className="w-full h-56 cursor-pointer"
                onClick={() => handleImageClick(selectedProject.projectImage)}
              >
                <img
                  src={selectedProject.projectImage}
                  alt={selectedProject.projectName}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                />
              </div>
            </div>

            {/* Project Header */}
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">{selectedProject.projectName}</h2>
              <p className="text-gray-600 text-sm md:text-base">{selectedProject.projectDescription}</p>
            </div>

            {/* Project Information */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { label: "Description", value: selectedProject.projectDescription, icon: <FileTextOutlined /> },
                { label: "Project Code", value: selectedProject.projectCode, icon: <ProjectOutlined /> },
                { label: "Job Number", value: selectedProject.jobNo, icon: <FolderOpenOutlined /> },
                { label: "Project Manager", value: selectedProject.projectManager, icon: <UserOutlined /> },
                { label: "Client", value: selectedProject.client, icon: <TeamOutlined /> },
                { label: "Engineer", value: selectedProject.engineer, icon: <UserOutlined /> },
                { label: "Location", value: selectedProject.location, icon: <EnvironmentOutlined /> },
                { label: "Contract Form", value: selectedProject.contractForm, icon: <FolderOpenOutlined /> },
                { label: "Contract Ref", value: selectedProject.contractRef, icon: <FolderOpenOutlined /> },
                { label: "Type", value: selectedProject.type, icon: <ProjectOutlined /> },
                { label: "Design Consultant", value: selectedProject.designConsultant, icon: <TeamOutlined /> },
                { label: "Time Granted", value: selectedProject.timeGranted, icon: <CalendarOutlined /> },
                {
                  label: "Start Date",
                  value: selectedProject.startDate ? moment(selectedProject.startDate).format("DD/MM/YYYY") : "NA",
                  icon: <CalendarOutlined />,
                },
                {
                  label: "End Date",
                  value: selectedProject.endDate ? moment(selectedProject.endDate).format("DD/MM/YYYY") : "NA",
                  icon: <CalendarOutlined />,
                },
              ].map((item, index) => (
                <div key={index} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="text-teal-600 text-lg">{item.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-500">{item.label}</div>
                      <div className="text-gray-900 font-semibold text-base truncate">{item.value}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4">
              <Button
                type="primary"
                size="large"
                disabled={!selectedProject.active}
                className={`flex-1 rounded-lg h-11 text-base font-medium ${
                  selectedProject.active
                    ? "bg-teal-600 hover:bg-teal-700 border-none"
                    : "bg-gray-300 border-none cursor-not-allowed"
                }`}
                icon={<RightOutlined />}
                onClick={() =>
                  selectedProject.active && handleSelectProject(selectedProject)
                }
              >
                {selectedProject.active ? "Open Project" : "Inactive"}
              </Button>
              <Button
                size="large"
                className="px-6 border-gray-300 hover:border-gray-400 rounded-lg h-11 text-base font-medium transition-all"
                onClick={() => setIsDrawerVisible(false)}
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Drawer>

      <style jsx global>{`
        .ant-card {
          max-width: 100%;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .ant-pagination-item,
        .ant-pagination-prev,
        .ant-pagination-next {
          border-radius: 8px !important;
          font-weight: 500;
        }
        .ant-pagination-item-active {
          background-color: #14b8a6 !important;
          border-color: #14b8a6 !important;
        }
        .ant-pagination-item-active a {
          color: white !important;
        }
        .ant-modal-content {
          background: transparent !important;
          box-shadow: none !important;
        }
        .ant-modal-mask {
          background: rgba(0, 0, 0, 0.7) !important;
        }
        @media (max-width: 640px) {
          .ant-card {
            margin: 0 auto;
          }
          .ant-pagination-item,
          .ant-pagination-prev,
          .ant-pagination-next {
            min-width: 32px;
            height: 32px;
            line-height: 32px;
          }
        }
      `}</style>
    </div>
  );
};

export default LandingPage;